import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { Portal } from 'react-portal';
import Helmet from '@plone/volto/helpers/Helmet/Helmet';
import { Container, Segment } from 'semantic-ui-react';
import { defineMessages, injectIntl } from 'react-intl';
import Error from '@plone/volto/components/theme/Error/Error';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import Toolbar from '@plone/volto/components/manage/Toolbar/Toolbar';
import backSVG from '@plone/volto/icons/back.svg';
import { updateProdukt, getVistPermissions } from '../../../actions';
import { canCrudProdukteTable } from '../../../helpers/permissions';
import GebindeForm from './GebindeForm';
import ButtonArea from '../ButtonArea';

const messages = defineMessages({
  back: {
    id: 'Back',
    defaultMessage: 'Back',
  },
});

const GebindeCreate = ({
  pathname,
  updateProdukt,
  intl,
  token,
  permissions = null,
  getVistPermissions,
}) => {
  const [formData, setFormData] = useState({
    produkt_id: '',
    artikelnummer: '',
    name: '',
    zugabe: '',
  });

  const location = useLocation();
  const [error, setError] = useState(null);
  const [errorDetail, setErrorDetail] = useState(null);
  const [isClient, setIsClient] = useState(false);
  const [produkt_id, setProdukt_Id] = useState(null);

  const history = useHistory();
  // Gebinde and Verkaufsinformation are managed under Produkte
  // CRUD (Admin/F&E/MK per TASK-90 §2).
  const canEdit = canCrudProdukteTable(permissions);
  // flag to make sure permissions are fully loaded before returning
  // an unauthorized error
  const [permissionsLoading, setPermissionsLoading] = useState(true);

  // load permissions
  useEffect(() => {
    setIsClient(true);
    // Fetch @vist_permissions if the reducer hasn't seen a response yet.
    if (!permissions && permissionsLoading) {
      getVistPermissions()
        .then(() => {
          setPermissionsLoading(false);
        })
        .catch(() => {
          setError({ status: 401 });
          setPermissionsLoading(false);
        });
    }
    // eslint-disable-next-line
  }, []);
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const id = searchParams.get('id');
    setProdukt_Id(id);
    formData.produkt_id = id;
    setIsClient(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'artikelnummer') {
      setFormData((prevFormData) => ({
        ...prevFormData,
        [name]: value.replace(/\D/g, ''),
      }));
    } else {
      setFormData((prevFormData) => ({ ...prevFormData, [name]: value }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Build gebinde data for creation (exclude produkt_id as it's set by backend)
    const gebindeData = {
      artikelnummer: formData.artikelnummer,
      name: formData.name,
    };
    if (formData.zugabe) {
      gebindeData.zugabe = formData.zugabe;
    }

    // Use updateProdukt with gebinde_operations for atomic update
    const payload = {
      gebinde_operations: {
        create: [gebindeData],
        update: [],
        delete: [],
      },
    };

    updateProdukt(produkt_id, payload)
      .then(() => {
        history.push(
          `/controlpanel/rezeptdatenbank/produkte/details?id=${produkt_id}`,
        );
      })
      .catch((error) => {
        setError('Es ist etwas schiefgelaufen, Gebinde wurde nicht angelegt');
        setErrorDetail(error.name + ': ' + error.message);
      });
  };
  if (!token) {
    return <Error error={{ status: 401 }} />;
  }
  if (!permissionsLoading && !canEdit) {
    return <Error error={{ status: 401 }} />;
  }
  return (
    <div className="view-wrapper rbd add">
      <Helmet title="Gebinde Anlegen" />
      <Container className="controlpanel">
        <Segment.Group raised>
          <Segment className="primary">
            {' '}
            <div className="rezeptdatenbank header">
              <div className="header-container">
                <h1>Gebinde Anlegen</h1>
              </div>
              <div className="button-container">
                <ButtonArea
                  isAdd={true}
                  handleSubmit={handleSubmit}
                  isSubmitDisabled={
                    formData.artikelnummer === '' || formData.name === ''
                  }
                  backURL={`/controlpanel/rezeptdatenbank/produkte/details?id=${produkt_id}`}
                />
              </div>
            </div>
          </Segment>
          <Segment className="rdbAddEditForm">
            {error && (
              <div className="error-message">
                <h2>{error}</h2>
                <p>{errorDetail}</p>
              </div>
            )}
            <div className="rdb add form gebinde">
              <GebindeForm
                formData={formData}
                handleInputChange={handleInputChange}
              />
            </div>
          </Segment>
        </Segment.Group>
      </Container>
      {isClient && (
        <Portal node={document.getElementById('toolbar')}>
          <Toolbar
            pathname={pathname}
            hideDefaultViewButtons
            inner={
              <Link to="/controlpanel/rezeptdatenbank" className="item">
                <Icon
                  name={backSVG}
                  className="contents circled"
                  size="30px"
                  title={intl.formatMessage(messages.back)}
                />
              </Link>
            }
          />
        </Portal>
      )}
    </div>
  );
};

GebindeCreate.propTypes = {
  pathname: PropTypes.string.isRequired,
  updateProdukt: PropTypes.func.isRequired,
  intl: PropTypes.object.isRequired,
  token: PropTypes.string,
  permissions: PropTypes.object,
  getVistPermissions: PropTypes.func,
};

export default compose(
  injectIntl,
  connect(
    (state) => ({
      pathname: state.router.location.pathname,
      token: state.userSession.token,
      permissions: state.permissions.permissions,
    }),
    { updateProdukt, getVistPermissions },
  ),
)(GebindeCreate);
